/**
 * safeRedirect: sanitize a `?redirect=` query value before navigating.
 *
 * The login page accepts a `?redirect=` query param that ProtectedRoutes
 * sets when bouncing an unauthenticated user away from a protected page.
 * Without validation, an attacker-controlled link like
 * `/login?redirect=https://evil.example/phish` would let us redirect a
 * freshly-authenticated user to an external origin: a classic open-redirect.
 *
 * The validation rules are intentionally narrow:
 *
 *   1. Must be a non-empty string.
 *   2. Must start with exactly one '/'. Protocol-relative URLs (`//evil.com`)
 *      and scheme URLs (`http:`, `https:`, `javascript:`, `data:`) are
 *      rejected: they could navigate to an external origin.
 *   3. Backslashes are rejected. Browsers normalize `\` to `/`, so a value
 *      like `/\evil.com` becomes the protocol-relative `//evil.com` when
 *      handed to `window.location` or a bare `<a href>`. A legitimate
 *      same-origin path never contains a backslash, so reject any.
 *   4. Whitespace and control characters are rejected. Browsers may strip
 *      these and re-parse a value like `/<TAB>javascript:alert(1)` into a
 *      scheme URL.
 *
 * We deliberately do NOT validate "is this a real route": overly strict and
 * brittle. Any same-origin path is acceptable; the worst case for a fake path
 * is the catch-all 404 page, which is harmless.
 *
 * @param {string|null|undefined} raw - The raw value, typically from
 *   `searchParams.get('redirect')`.
 * @returns {string|null} The sanitized path, or null if invalid.
 */
export function sanitizeRedirectPath(raw) {
    if (typeof raw !== 'string' || raw.length === 0) {
        return null;
    }
    // Reject backslashes outright. Browsers normalize `\` to `/`, so
    // `/\evil.com` resolves to the protocol-relative `//evil.com` and would
    // point at an external host. A same-origin path never contains one, so
    // this is safe to reject wholesale. Done before the `//` check below so
    // that backslash-disguised protocol-relative forms cannot slip past it.
    if (raw.includes('\\')) {
        return null;
    }
    // Reject anything that is not a same-origin absolute path. A leading
    // single '/' followed by something that is not another '/' is the only
    // shape allowed. '//' is the protocol-relative form and would point at
    // an external host.
    if (!raw.startsWith('/') || raw.startsWith('//')) {
        return null;
    }
    // Reject any whitespace OR control character (0x00-0x1F, 0x7F). URL
    // parsers in some browsers strip these and re-tokenize the remainder,
    // which can sneak a scheme URL past a naive prefix check.
    // eslint-disable-next-line no-control-regex
    if (/[\s\x00-\x1f\x7f]/.test(raw)) {
        return null;
    }
    return raw;
}

export default sanitizeRedirectPath;
